import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Hook to fetch published feeds from backend API for public portal
 * Transforms backend data into FeedEngine-compatible format
 */
export const usePublicFeeds = () => {
  const [feeds, setFeeds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeeds = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/website/public/feeds`);
        
        // Transform backend data to FeedEngine format
        const transformed = transformFeedsForEngine(response.data.feeds || []);
        setFeeds(transformed);
        setError(null);
      } catch (err) {
        console.error('[usePublicFeeds] Failed to fetch:', err.message);
        setError(err.message);
        // Fallback to empty structure on error
        setFeeds({ HOME: { id: 'home', name: 'HOME', posts: [] } });
      } finally {
        setLoading(false);
      }
    };

    fetchFeeds();
  }, []);

  return { feeds, loading, error };
};

/**
 * Transform backend feed hierarchy into FeedEngine format
 * Backend: { id, title, type, children: [], shortCaption, media: {type, url, poster}, cta, ... }
 * FeedEngine: { id, name, posts: [{id, title, shortCaption, fullInformation, media, cta, childFeed}] }
 */
// Stable nav section key for a root feed, derived from its title so labels can be renamed later
const SECTION_KEYS = {
  'OUR WORK': 'work',
  'WORK': 'work',
  'SYSTEMS': 'systems',
  'PORTFOLIO': 'portfolio',
  'ABOUT': 'about',
  'BUILD WITH US': 'build'
};

export function sectionKeyFor(title) {
  const upper = (title || '').trim().toUpperCase();
  return SECTION_KEYS[upper] || upper.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

// EXPLORE only makes sense when the child feed has something to show
function exploreCta(feed) {
  return feed.posts.length > 0
    ? { label: 'EXPLORE', type: 'OPEN_FEED', destination: feed.id }
    : { label: 'EXPLORE', type: 'NONE', destination: null };
}

function transformFeedsForEngine(backendFeeds) {
  const feedMap = {};
  
  // First pass: create all feed objects
  const createFeed = (backendFeed) => {
    const feedId = backendFeed.id;
    
    // Determine if this is a feed container (CATEGORY) or a post (PHOTO/VIDEO/WORK/POST)
    const isContainer = ['CATEGORY', 'INTRO'].includes(backendFeed.type);
    
    if (isContainer) {
      // This is a feed container with children
      const feed = {
        id: feedId,
        name: backendFeed.title || 'Untitled Feed',
        sectionKey: sectionKeyFor(backendFeed.title),
        posts: []
      };
      
      feedMap[feedId] = feed;
      
      // Process children
      if (backendFeed.children && backendFeed.children.length > 0) {
        backendFeed.children.forEach(child => {
          const isChildContainer = ['CATEGORY', 'INTRO'].includes(child.type);
          
          if (isChildContainer) {
            // Nested feed - create it and add navigation post
            const childFeed = createFeed(child);
            feed.posts.push({
              id: `nav-${child.id}`,
              title: child.title || 'Explore',
              subtitle: child.subtitle,
              shortCaption: child.shortCaption,
              media: child.media || { type: 'none' },
              cta: exploreCta(childFeed),
              childFeed: childFeed
            });
          } else {
            // Regular post
            feed.posts.push(transformPost(child));
          }
        });
      }
      
      return feed;
    } else {
      // This is a standalone post (shouldn't happen at root, but handle gracefully)
      return null;
    }
  };
  
  // Build ROOT feed structure
  const rootFeed = {
    id: 'home',
    name: 'HOME',
    posts: []
  };
  
  // Process all root-level feeds
  backendFeeds.forEach(backendFeed => {
    const isContainer = ['CATEGORY', 'INTRO'].includes(backendFeed.type);
    
    if (isContainer) {
      // Container feed - create it and add as navigation post
      const feed = createFeed(backendFeed);
      if (feed) {
        rootFeed.posts.push({
          id: `nav-${backendFeed.id}`,
          sectionKey: feed.sectionKey,
          title: backendFeed.title || 'Explore',
          subtitle: backendFeed.subtitle,
          shortCaption: backendFeed.shortCaption,
          media: backendFeed.media || { type: 'none' },
          cta: exploreCta(feed),
          childFeed: feed
        });
      }
    } else {
      // Regular post at root level - add directly to HOME
      rootFeed.posts.push(transformPost(backendFeed));
    }
  });
  
  // Return in FEEDS object format expected by useFeedNavigation
  return {
    HOME: rootFeed,
    ...feedMap
  };
}

/**
 * Transform a single backend post to FeedEngine format
 */
function transformPost(backendPost) {
  return {
    id: backendPost.id,
    title: backendPost.title,
    subtitle: backendPost.subtitle,
    shortCaption: backendPost.shortCaption,
    fullInformation: backendPost.fullInformation,
    media: {
      type: backendPost.media?.type || 'none',
      url: backendPost.media?.url,
      poster: backendPost.media?.poster,
      autoplay: backendPost.autoplay,
      loop: backendPost.loop
    },
    cta: backendPost.cta ? {
      label: backendPost.cta.label,
      destination: backendPost.cta.destination
    } : null,
    category: backendPost.type
  };
}

export default usePublicFeeds;
